import { FC, useRef } from 'react';

import { Edit } from '@agendapro/emerald-icons';
import { useTranslation } from 'react-i18next';
import { BannerLogo, ViewMore } from '@/UI';
import { CompanyInfoProps } from './types';
import * as St from './CompanyInfo.styles';
import { ShowUp } from '@/components';
import { SliderAvatarEmployees } from '../ServicesByLocation/SliderAvatarEmployees';
import { useCompanyBookingInfo } from '@/services/company';
import { useAuthContext, usePageContext } from '@/hooks';
import { EditProfileButton } from '@/UI/Atoms/EditProfileButton';
import { canEditOwnCompany, editCompanyUrls } from '@/utils/canEditOwnCompany';

export const CompanyInfo: FC<CompanyInfoProps> = ({
  logo,
  banner,
  collapsed,
  rrss,
  companyDescription = '',
  locationDescription = '',
  isLoading,
  companyName,
  mobileAddress,
  locationId,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { companySlug, selectedServices, companyOverview } = usePageContext();
  const { auth } = useAuthContext();
  const { data: companyBookingInfo } = useCompanyBookingInfo(companySlug);
  const canEdit = canEditOwnCompany({
    userRoleId: auth.userData?.roleId,
    userCompanyId: auth.userData?.companyId,
    currentCompanyId: companyOverview?.id,
  });

  return (
    <St.CompanyInfoContainer collapsed={collapsed} hasBanner ref={containerRef}>
      <St.HeroWrapper>
        <BannerLogo banner={banner} logo={logo} canEdit={canEdit} locationId={locationId} />
      </St.HeroWrapper>
      <ViewMore
        textContent={companyDescription}
        textContentSecondary={locationDescription}
        maxNumOfLines={2}
        arial-label="companyDescription"
        className="companyDescription"
        data-testid="companyDescription"
        isLoading={isLoading}
        rrss={rrss}
        companyName={companyName}
        showReviews
        logo={logo}
        canEdit={canEdit}
      />
      {canEdit && (
        <EditProfileButton
          className="flex lg:hidden h-fit w-full mb-2 bg-background-primary border border-border-brand border-solid rounded-md p-1 flew-row items-center justify-center gap-1 text-text-brand text-sm hover:bg-background-secondary transition-colors duration-300"
          url={editCompanyUrls.company}
          mixpanelEvent="editCompany"
        >
          <Edit size={16} className="[&>path]:fill-icon-brand" />
          {t('EDIT_COMPANY')}
        </EditProfileButton>
      )}
      {mobileAddress && <ShowUp untilTo="XLarge">{mobileAddress}</ShowUp>}
      {companyBookingInfo?.showProviders !== 2 && (
        <ShowUp untilTo="XLarge">
          <SliderAvatarEmployees hasSelectedServices={!!selectedServices} canEdit={canEdit} />
        </ShowUp>
      )}
    </St.CompanyInfoContainer>
  );
};
